import { checkIsBusinessUser, getUserData } from "selectors/user";
import { createSelector } from "reselect";
import { getActiveUsersData } from "selectors/organizations";
import { getTextsData } from "./texts";
import Arrays from "utils/Arrays";
import DateTimeFormats from "const/DateTimeFormats";
import IntegrationServices from "const/IntegrationServices";
import TaskStatuses from "const/TaskStatuses";
import TaskTypes from "const/TaskTypes";
import Texts from "utils/Texts";
import UserRoles from "const/UserRoles";
import moment from "moment";

export const getTasksRawData = ({ tasks: { data } }) => data;

export const checkTasksFetching = ({ tasks: { fetchingData } }) => fetchingData;

export const checkTasksReadyToDisplay = ({ tasks: { tasksReadyToDisplay } }) => tasksReadyToDisplay;

export const getAutoTasksRawData = createSelector(
  [getTasksRawData],
  (data) => data.filter(({ type = TaskTypes.CUSTOM }) => type && type !== TaskTypes.CUSTOM)
);

export const getTasksDataWithTargetUser = createSelector(
  [
    getTasksRawData,
    getActiveUsersData,
    checkIsBusinessUser
  ],
  (data, users, businessUser) => {
    return data
      .filter(({ type = TaskTypes.CUSTOM }) => !businessUser || type === TaskTypes.CUSTOM)
      .map(({ createdBy, targetUser, type = TaskTypes.CUSTOM, ...restData }) => {
        const createdByUserData = users.find(({ id }) => id === createdBy) || null;

        const targetUserData = users.find(({ id }) => id === targetUser) || null;

        return {
          ...restData,
          type,
          createdBy: createdByUserData,
          targetUser: targetUserData,
          autoTask: type !== TaskTypes.CUSTOM
        };
      });
  }
);

export const getTasksDataWithAutoTask = createSelector(
  [
    getTasksDataWithTargetUser,
    getTextsData,
    ({ businesses: { data } }) => data,
    ({ businesses: { selectedBusinessId } }) => selectedBusinessId
  ],
  (tasks, { uiTexts, messages }, businesses, selectedBusinessId) => {
    const { extraData: { integrationService } = {} } = businesses.find(({ id }) => id === selectedBusinessId) || {};

    const { shortLabel: serviceName } = IntegrationServices.getByValue(integrationService) || {};

    return tasks.map((task) => {
      const { status, type = TaskTypes.CUSTOM } = task;

      if (type === TaskTypes.CUSTOM) return task;
      if (status === TaskStatuses.COMPLETED && (type !== TaskTypes.BANK_FEEDS)) return null;

      const monthYearStartDateText = task.startDate
        ? moment(task.startDate).format(DateTimeFormats.MONTH_AND_YEAR_TEXT)
        : "";

      return {
        ...task,
        title: ({
          [TaskTypes.TRANSACTIONS]: Texts.replaceVars(
            uiTexts.categorizeTransactionsForDate,
            { date: monthYearStartDateText }
          ),
          [TaskTypes.DOCUMENTS]: Texts.replaceVars(
            uiTexts.reviewUploadedDocumentsForDate,
            { date: monthYearStartDateText }
          ),
          [TaskTypes.AUDIT]: Texts.replaceVars(
            uiTexts.resolveCleanUpIssuesForDate,
            { date: monthYearStartDateText }
          ),
          [TaskTypes.BANK_FEEDS]: Texts.replaceVars(
            uiTexts.renewServiceBankConnections,
            { serviceName }
          )
        })[type] || "",
        text: ({
          [TaskTypes.TRANSACTIONS]: messages.uncategorizedAutoTaskDescription,
          [TaskTypes.DOCUMENTS]: messages.ocrAiAutoTaskDescription,
          [TaskTypes.AUDIT]: messages.cleanUpAutoTaskDescription,
          [TaskTypes.BANK_FEEDS]: messages.bankFeedsAutoTaskDescription
        })[type] || ""
      };
    }).filter(Boolean);
  }
);

export const getTasksData = createSelector(
  [getTasksDataWithAutoTask, getUserData],
  (tasks, user) => {
    return Arrays.sort(tasks, [
      ({ status }) => Object.values(TaskStatuses).indexOf(status),
      ({ dueDate }) => dueDate ? moment.utc(dueDate).unix() : Infinity,
      ({ startDate }) => startDate ? moment.utc(startDate).unix() : Infinity,
      ({ targetUser }) => targetUser?.id === user.id || (!targetUser && !UserRoles.checkIsBusiness(user.role))
    ], [true, true, true, true, false]);
  }
);
